import './Badge.css';
import type { BadgeVariant } from './Badge.js';

/**
 * Renders every controlled-vocabulary state from
 * docs/platform/08-information-architecture.md §6. Namespaced by `family`
 * because several families share a literal string ("failed", "paused",
 * "cancelled" all appear in more than one family) with potentially different
 * meaning/colour — a bare `value` prop without a family would be ambiguous.
 *
 * Scope note (honest, not silent): only the families load-bearing for the
 * current MVP build phase (action, source, sync run, finding — F02–F04) are
 * implemented. The roadmap-phase families in §6.3 (quality/load/quarantine/
 * surface/agent-credential/deployment/value-estimate/PII/export) are listed
 * in FAMILY_TODO below rather than guessed at — each is a real extension
 * point to fill in when its owning feature (F17–F25) is actually built,
 * matching 08 §4's own "phase boundary, not a gap" framing for those
 * features. test/StateChip.test.tsx asserts every value in every
 * IMPLEMENTED family renders without throwing, so adding a new value to an
 * existing family's type without updating its label map fails the test
 * immediately rather than rendering silently blank.
 */

export type ActionState =
  | 'proposed'
  | 'approved'
  | 'dry_run'
  /* ⚠️ **Exactly the plane's `action.state` CHECK, and nothing else** (reconciled 2026-08-13, B-260).
     This family's only sources are API responses — `SafetyGate` and the hosted Actions screen — and
     `GET /v1/actions` returns plane rows unmodified, so any value outside the CHECK is a label
     nothing can render and any value inside it that is missing is an unlabelled chip.

     `sampling`, `applying` and `conflict` were missing: a row in any of the three rendered a chip
     with NO label, and `conflict` is [ADR-012]'s apply-time drift state — the record changed under an
     approved batch and the change was NOT made, which is the outcome a reader most needs named.

     `sample_applied`, `awaiting_scopes`, `paused` and `cancelled` were here and are gone. The first
     is `packages/executor`'s own word, translated to `sampling` by `schemaActionState()` before any
     write; the other three have no writer in any layer. ⚠️ Removing a NAME is not a statement that a
     behaviour is gone — F01's write-scope escalation is built, it just parks the connection rather
     than the action. If that changes, this union, the contract's enum and the CHECK grow together;
     `test/actionVocabulary.test.ts` in apps/web is what makes that a build failure rather than a
     silent divergence. */
  | 'sampling'
  | 'applying'
  | 'applied'
  | 'partially_applied'
  | 'failed'
  | 'rolled_back'
  | 'conflict';

export type SourceState = 'pending' | 'connected' | 'paused' | 'degraded' | 'auth_error' | 'disconnected';

export type SyncRunState = 'queued' | 'running' | 'completed' | 'partial' | 'failed' | 'cancelled';

export type FindingState = 'open' | 'in_progress' | 'resolved' | 'dismissed' | 'snoozed';

export const FAMILY_TODO = [
  'quality_score',
  'load_evaluation',
  'quarantined_row',
  'surface',
  'agent_credential',
  'merge_conflict_review',
  'deployment_tier',
  'byoc_deployment',
  'value_estimate_availability',
  'pii_class',
  'pii_handling',
  'export',
] as const;

interface StateDef {
  label: string;
  variant: BadgeVariant;
}

// Labels for action states are the exact UI labels from 08 §6.2's table.
const ACTION_STATES: Record<ActionState, StateDef> = {
  proposed: { label: 'Proposed', variant: 'neutral' },
  approved: { label: 'Approved', variant: 'accent' },
  dry_run: { label: 'Dry run', variant: 'info' },
  sampling: { label: 'Applying to a sample', variant: 'info' },
  applying: { label: 'Applying', variant: 'info' },
  /* Not `danger`: nothing broke and nothing was written. The batch is waiting for a person because
     the record moved — ADR-012's re-review path. `warning` is the honest tone. */
  conflict: { label: 'Needs re-review — the record changed', variant: 'warning' },
  applied: { label: 'Applied', variant: 'success' },
  rolled_back: { label: 'Rolled back', variant: 'neutral' },
  partially_applied: { label: 'Partially applied', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
};

// Source/sync-run/finding states have no UI-label table in 08 — title-cased
// from the documented vocabulary strings (08 §6.3) rather than invented.
const SOURCE_STATES: Record<SourceState, StateDef> = {
  pending: { label: 'Pending', variant: 'neutral' },
  connected: { label: 'Connected', variant: 'success' },
  paused: { label: 'Paused', variant: 'warning' },
  degraded: { label: 'Degraded', variant: 'warning' },
  auth_error: { label: 'Auth error', variant: 'danger' },
  disconnected: { label: 'Disconnected', variant: 'neutral' },
};

const SYNC_RUN_STATES: Record<SyncRunState, StateDef> = {
  queued: { label: 'Queued', variant: 'neutral' },
  running: { label: 'Running', variant: 'accent' },
  completed: { label: 'Completed', variant: 'success' },
  partial: { label: 'Partial', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};

const FINDING_STATES: Record<FindingState, StateDef> = {
  open: { label: 'Open', variant: 'accent' },
  in_progress: { label: 'In progress', variant: 'warning' },
  resolved: { label: 'Resolved', variant: 'success' },
  dismissed: { label: 'Dismissed', variant: 'neutral' },
  snoozed: { label: 'Snoozed', variant: 'info' },
};

/**
 * Org lifecycle (08 §6). `closing` is the one worth reading carefully: it is
 * ADR-039's staged purge in progress, not a synonym for closed — the tenant's data
 * still exists and rollback windows may still be live, which is exactly why an
 * operator must be able to tell the two apart at a glance.
 */
type OrgLifecycleState = 'active' | 'suspended' | 'closing' | 'closed';

const ORG_LIFECYCLE_STATES: Record<OrgLifecycleState, StateDef> = {
  active: { label: 'Active', variant: 'success' },
  suspended: { label: 'Suspended', variant: 'warning' },
  closing: { label: 'Closing', variant: 'danger' },
  closed: { label: 'Closed', variant: 'neutral' },
};

/** Deployment tier (ADR-041). Not a state machine — a tenant's chosen tier. */
type DeploymentTierValue = 'entry_sandbox' | 'managed_cloud' | 'byo_cloud';

const DEPLOYMENT_TIERS: Record<DeploymentTierValue, StateDef> = {
  // "Sandbox" rather than "Entry": the tier's purpose is a trust-building audit
  // sandbox, and "Entry" reads as a paid plan rung, which it is not.
  entry_sandbox: { label: 'Sandbox (PWA)', variant: 'neutral' },
  managed_cloud: { label: 'Managed Cloud', variant: 'info' },
  byo_cloud: { label: 'BYO-Cloud', variant: 'accent' },
};

/**
 * Deployment state (F30) — the state of ONE region's resources, not of a client.
 *
 * Added 2026-07-30 with multi-region ([ADR-068]): a client may hold several deployment
 * records, and the detail screen renders each region's state, so this needed a family. Values
 * mirror the control plane's `deployment_state` enum exactly — a contract test asserts the two
 * are equal, because the previous divergence between them was invisible to every test and
 * bridged in the live client by a cast.
 *
 * `suspended` is `warning` not `danger`: it is reversible and usually deliberate.
 * `decommissioning` and `torn_down` are separated because one is in progress and the other is
 * finished, and an operator deciding whether to intervene needs to know which.
 */
type DeploymentStateValue =
  | 'provisioning'
  | 'active'
  | 'degraded'
  | 'suspended'
  | 'decommissioning'
  | 'torn_down';

const DEPLOYMENT_STATES: Record<DeploymentStateValue, StateDef> = {
  provisioning: { label: 'Provisioning', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  degraded: { label: 'Degraded', variant: 'warning' },
  suspended: { label: 'Suspended', variant: 'warning' },
  decommissioning: { label: 'Decommissioning', variant: 'danger' },
  torn_down: { label: 'Torn down', variant: 'neutral' },
};

/** Provisioning run (F30). `partially_applied` is a real outcome, not an error. */
type ProvisioningRunState = 'queued' | 'running' | 'succeeded' | 'failed' | 'partially_applied';

const PROVISIONING_STATES: Record<ProvisioningRunState, StateDef> = {
  queued: { label: 'Queued', variant: 'neutral' },
  running: { label: 'Running', variant: 'info' },
  succeeded: { label: 'Succeeded', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
  // Warning, not danger: half the work really was done and will be reused on
  // retry, so presenting it as an outright failure would invite a duplicate run.
  partially_applied: { label: 'Partially applied', variant: 'warning' },
};

/**
 * Operator audit outcome (F30). Exactly two values, and the contract says so —
 * `openapi-internal.yaml`'s `OperatorAuditEvent.outcome` is `enum [success,
 * failure]`, unlike its sibling `action`, which is deliberately an open string.
 * A closed vocabulary is what makes this safe to route through a component that
 * throws on anything unmapped.
 *
 * The label carries the meaning; the variant only reinforces it. A failed operator
 * action must not be distinguishable by colour alone (WCAG 1.4.1), and this is a
 * trail somebody reads to find the one row that went wrong.
 */
type OperatorOutcome = 'success' | 'failure';

const OPERATOR_OUTCOMES: Record<OperatorOutcome, StateDef> = {
  success: { label: 'Succeeded', variant: 'success' },
  failure: { label: 'Failed', variant: 'danger' },
};

/**
 * Subscription state (08 §6, and the operator contract's own enum).
 *
 * Two of these are easy to render wrongly and both matter to a paying customer.
 * `grace` is **not** a failure — F13's dunning keeps syncing on through the grace
 * window, so danger colouring would misrepresent a client who is merely late.
 * `read_only` is the state after grace expires: nothing is deleted, so it is a
 * warning rather than a danger. And **`over_ceiling` is deliberately absent** — it
 * is a derived billing condition, not a state; a client over their ceiling stays
 * `active` because Dedupr sits in their data flow and stopping sync would be an
 * outage rather than a billing event.
 */
type SubscriptionStateValue =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'grace'
  | 'read_only'
  | 'canceled'
  | 'incomplete';

const SUBSCRIPTION_STATES: Record<SubscriptionStateValue, StateDef> = {
  trialing: { label: 'Trialing', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  past_due: { label: 'Past due', variant: 'warning' },
  grace: { label: 'In grace', variant: 'warning' },
  read_only: { label: 'Read-only', variant: 'warning' },
  canceled: { label: 'Canceled', variant: 'neutral' },
  incomplete: { label: 'Incomplete', variant: 'neutral' },
};

export type StateChipProps =
  | { family: 'action'; value: ActionState }
  | { family: 'source'; value: SourceState }
  | { family: 'syncRun'; value: SyncRunState }
  | { family: 'finding'; value: FindingState }
  | { family: 'orgLifecycle'; value: OrgLifecycleState }
  | { family: 'deploymentTier'; value: DeploymentTierValue }
  | { family: 'deploymentState'; value: DeploymentStateValue }
  | { family: 'provisioningRun'; value: ProvisioningRunState }
  | { family: 'operatorOutcome'; value: OperatorOutcome }
  | { family: 'subscriptionState'; value: SubscriptionStateValue };

const FAMILY_MAP = {
  action: ACTION_STATES,
  source: SOURCE_STATES,
  syncRun: SYNC_RUN_STATES,
  finding: FINDING_STATES,
  orgLifecycle: ORG_LIFECYCLE_STATES,
  deploymentTier: DEPLOYMENT_TIERS,
  deploymentState: DEPLOYMENT_STATES,
  provisioningRun: PROVISIONING_STATES,
  operatorOutcome: OPERATOR_OUTCOMES,
  subscriptionState: SUBSCRIPTION_STATES,
} as const;

export function StateChip(props: StateChipProps) {
  const table = FAMILY_MAP[props.family] as Record<string, StateDef>;
  const def = table[props.value];
  if (!def) {
    // Fails loudly rather than rendering blank — exactly the case this
    // component exists to prevent going unnoticed.
    throw new Error(`StateChip: no label mapped for family="${props.family}" value="${props.value}"`);
  }
  return (
    <span className={`dpr-badge dpr-badge--${def.variant}`} data-family={props.family} data-value={props.value}>
      {def.label}
    </span>
  );
}
